import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Plus, Check, X, FileText, Search, Edit2, Trash2, Receipt,
  Clock, AlertCircle, CheckCircle, Send, Copy, Link,
  ChevronDown, ExternalLink, UploadCloud, Mail, MessageSquare, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import EmailPicker, { looksLikeEmail } from '../components/EmailPicker';
import { businessToday, isoPlusDays, parseDbTs } from '../utils/time';
import { UploadReviewScreenshot } from '../components/ReviewClaimsPanel';
import PaidOutsideCard from '../components/PaidOutsideCard';
import TooSoonCard from '../components/TooSoonCard';
import LateFeesCard from '../components/LateFeesCard';
import PriceCheckCard from '../components/PriceCheckCard';
import ReviewSendCard from '../components/ReviewSendCard';
import FollowUpsCard from '../components/FollowUpsCard';
import AutopayCard from '../components/AutopayCard';
import PayoutsCard from '../components/PayoutsCard';
import MoneyStatusCard from '../components/MoneyStatusCard';
import { noChargeWords } from '../components/PaymentAutopayBlock';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Invoice {
  id: number; invoice_number: string; customer_id: number; customer_name: string;
  billing_period_start?: string; billing_period_end?: string;
  subtotal: number; total: number; status: string; invoice_type?: string;
  due_date?: string; paid_at?: string; created_at?: string; notes?: string; line_item_count: number;
  share_token?: string;
  discount_type?: string; discount_amount?: number; discount_label?: string;
  tip_amount?: number; tip_pct?: number | null;
}
interface LineItem {
  id: number; invoice_id: number; job_id?: number; description: string;
  quantity?: number; unit_price?: number; amount: number; sort_order?: number;
  service_date?: string | null;   // the day the work was done (owner, Oct 2 2026); a month line has none
}
interface Job {
  id: number; scheduled_date: string; time_start?: string; price_snapshot?: number;
  service_type?: string; address?: string; property_label?: string;
}
interface Customer { id: number; name: string; email?: string; phone?: string; }
interface UnbilledItem {
  id: number; job_id?: number; project_id?: number; customer_id: number; customer_name: string;
  property_address: string; description: string; service_type?: string;
  scheduled_date?: string; amount: number | null; completed_at: string;
  source_type: 'job' | 'project';
}

/** What a void does with the bill's visits (server: PUT /invoices/:id/status { visits }). */
type VoidVisits = 'rebill' | 'no_bill' | 'free';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d?: string) => {
  if (!d) return '—';
  const s = d.includes(' ') ? d.split(' ')[0] : d;
  return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtMoney = (n?: number | null) => (n != null ? `$${n.toFixed(2)}` : '—');
/** "Sep 10" — a line's service date; blank when the line has none (a month line, a credit). */
const fmtDay = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
/** What a date input holds: YYYY-MM-DD or nothing. */
const dayInput = (d?: string | null) => (d ? String(d).slice(0, 10) : '');

// Owner, Oct 2 2026: "add date selection on billing side aswell for filters".
// A bill is matched on the dates it carries: the month it covers (its period),
// the day it was made, or the day it was paid — the owner picks which.
type DateBy = 'period' | 'made' | 'paid';
type DatePick = 'all' | 'this_month' | 'last_month' | 'last_3' | 'this_year' | 'custom';
/** The YYYY-MM-DD (Chicago) a date column or a timestamp falls on. */
const dayOf = (v?: string | null): string | null => {
  if (!v) return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T00:00:00/.test(s)) return s.slice(0, 10);   // a date column read back as midnight UTC
  const d = parseDbTs(s);
  return d ? businessToday(d) : s.slice(0, 10);
};
/** From/to for a quick pick, today being the business day. '' = open-ended. Pure. */
function rangeFor(pick: DatePick, today = businessToday()): { from: string; to: string } {
  const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7));
  const first = (yy: number, mm: number) => `${yy}-${String(mm).padStart(2, '0')}-01`;
  const lastOf = (yy: number, mm: number) => isoPlusDays(first(mm === 12 ? yy + 1 : yy, mm === 12 ? 1 : mm + 1), -1);
  if (pick === 'this_month') return { from: first(y, m), to: lastOf(y, m) };
  if (pick === 'last_month') { const yy = m === 1 ? y - 1 : y, mm = m === 1 ? 12 : m - 1; return { from: first(yy, mm), to: lastOf(yy, mm) }; }
  if (pick === 'last_3') { let yy = y, mm = m - 2; if (mm < 1) { mm += 12; yy -= 1; } return { from: first(yy, mm), to: lastOf(y, m) }; }
  if (pick === 'this_year') return { from: `${y}-01-01`, to: `${y}-12-31` };
  return { from: '', to: '' };
}
/** Does a bill fall inside [from, to] by the chosen date? Open ends match everything that side. Pure. */
function billInRange(inv: Invoice, by: DateBy, from: string, to: string): boolean {
  if (!from && !to) return true;
  const within = (d: string | null) => !!d && (!from || d >= from) && (!to || d <= to);
  if (by === 'paid') return within(dayOf(inv.paid_at));
  if (by === 'made') return within(dayOf(inv.created_at));
  // Period: the bill's month overlaps the range; a bill with no period goes by the day it was made.
  const ps = dayOf(inv.billing_period_start), pe = dayOf(inv.billing_period_end) || ps;
  if (!ps) return within(dayOf(inv.created_at));
  return (!to || ps <= to) && (!from || (pe || ps) >= from);
}
const fmtMoneyK = (n?: number | null) => {
  if (n == null) return '—';
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(2)}`;
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  void: 'bg-gray-200 text-gray-500 line-through',
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Billing() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'invoices' | 'unbilled'>('invoices');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [datePick, setDatePick] = useState<DatePick>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateBy, setDateBy] = useState<DateBy>('period');
  const pickDates = (pick: DatePick) => { setDatePick(pick); if (pick !== 'custom') { const r = rangeFor(pick); setDateFrom(r.from); setDateTo(r.to); } };
  const [createOpen, setCreateOpen] = useState(false);
  // "Invoice Customer" on a profile lands here with the customer already picked.
  const [presetCustomer, setPresetCustomer] = useState<{ id: number; name: string } | null>(null);
  const [detail, setDetail] = useState<Invoice | null>(null);
  const [shareInvoice, setShareInvoice] = useState<Invoice | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const [sheetsMsg, setSheetsMsg] = useState<string | null>(null);

  const { data: invoicesData, isLoading: invLoading } = useQuery('invoices',
    () => api.get('/invoices').then(r => r.data)
  );
  const { data: dashData } = useQuery('billing-dashboard',
    () => api.get('/billing/dashboard').then(r => r.data)
  );
  const { data: customersData } = useQuery('customers-active',
    () => api.get('/customers').then(r => r.data.customers)
  );
  const { data: unbilledData, isLoading: unbilledLoading } = useQuery('billing-unbilled',
    () => api.get('/billing/unbilled').then(r => r.data)
  );
  const { data: stripeStatus } = useQuery('stripe-status',
    () => api.get('/integrations/stripe-status').then(r => r.data as { configured: boolean; mode: string; webhookConfigured: boolean }),
    { retry: false }
  );
  const { data: smsStatus } = useQuery('sms-status',
    () => api.get('/integrations/sms-status').then(r => r.data as { configured: boolean; phoneNumber?: string }),
    { retry: false }
  );
  const isStripeTestMode = stripeStatus?.mode === 'test';

  const customers: Customer[] = customersData || [];
  const allInvoices: Invoice[] = invoicesData?.invoices || [];
  const stats: any[] = invoicesData?.stats || [];
  const unbilled: UnbilledItem[] = unbilledData || [];
  const unbilledCount: number = dashData?.unbilled?.count || 0;

  // Arriving from a customer's profile with an invoice picked: open it.
  const location = useLocation();
  useEffect(() => {
    const id = (location.state as any)?.openInvoiceId;
    if (!id || !allInvoices.length) return;
    const inv = allInvoices.find(i => i.id === id);
    if (inv) { setDetail(inv); window.history.replaceState({}, ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, allInvoices.length]);
  useEffect(() => {
    const who = (location.state as any)?.newInvoiceFor;
    if (!who?.id) return;
    setPresetCustomer({ id: Number(who.id), name: String(who.name || '') });
    setCreateOpen(true);
    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const invoices = allInvoices.filter(inv => {
    const s = search.toLowerCase();
    if (s && !inv.customer_name?.toLowerCase().includes(s) && !inv.invoice_number?.toLowerCase().includes(s)) return false;
    if (statusFilter && inv.status !== statusFilter) return false;
    if (!billInRange(inv, dateBy, dateFrom, dateTo)) return false;
    return true;
  });
  const shownTotal = invoices.reduce((t, inv) => t + (Number(inv.total) || 0), 0);

  const sentAmt   = stats.find(s => s.status === 'sent')?.amount || 0;
  const paidAmt   = stats.find(s => s.status === 'paid')?.amount || 0;
  const draftCnt  = stats.find(s => s.status === 'draft')?.count || 0;
  const overAmt   = stats.find(s => s.status === 'overdue')?.amount || 0;

  // Voiding a bill with visits asks what about them (owner, Oct 1 2026): bill them on the next bill,
  // don't bill them (not owed), or make them free — the server does it per visit and says what it did.
  const updateStatus = useMutation(async ({ id, status, visits }: { id: number; status: string; visits?: VoidVisits }) => {
    const r = await api.put(`/invoices/${id}/status`, visits ? { status, visits } : { status });
    return r.data;
  }, {
    onSuccess: (d: any) => {
      qc.invalidateQueries('invoices');
      qc.invalidateQueries('billing-review');
      qc.invalidateQueries('billing-unbilled');
      qc.invalidateQueries('billing-dashboard');
      qc.invalidateQueries('review-claims');   // a Google-review $10 on a voided bill moves on or waits — the claims list says where
      // Worth a second look: a visit left as it was, or a free visit whose add-ons / travel still bill.
      const leftAlone = (d?.visits?.skipped || []).length > 0 || Number(d?.visits?.extras_still_bill) > 0;
      (leftAlone ? toast.warn : toast.success)(d?.message || 'Invoice updated', { autoClose: leftAlone ? 12000 : 5000 });
      setDetail(null);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to update'); },
  });

  // "Don't bill" on an unbilled visit (server/services/noBill): not owed — month-end leaves it alone until undone on the visit.
  const noBill = useMutation(({ jobId, reason }: { jobId: number; reason: string }) =>
    api.post(`/jobs/${jobId}/no-bill`, { reason }).then(r => r.data), {
    onSuccess: (d: any) => {
      qc.invalidateQueries('billing-unbilled');
      qc.invalidateQueries('billing-dashboard');
      toast.success(`${d?.message || 'Will not be billed'} — undo it on the visit.`);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); },
  });

  // Review & send's "Open to edit": the bill's own detail (the list may not have it yet — then ask for it).
  const openInvoice = async (id: number) => {
    const inv = allInvoices.find(i => i.id === id);
    if (inv) { setDetail(inv); return; }
    try {
      const r = await api.get(`/invoices/${id}`);
      if (r.data?.invoice) setDetail(r.data.invoice);
    } catch { toast.error('Could not open that bill'); }
  };
  // Needs Attention's "Bill drafted" line links here as /billing?invoice=ID: open that bill (owner, Oct 13 2026).
  // Its "bills are ready to check" line links as /billing?review=<period_end>: open Review & send on that month (Oct 26 2026).
  const [reviewWanted, setReviewWanted] = useState<string | null>(null);
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const id = Number(q.get('invoice'));
    const review = q.get('review');
    if (!id && !review) return;
    if (id) openInvoice(id);
    if (review) setReviewWanted(/^\d{4}-\d{2}-\d{2}$/.test(review) ? review : '');
    window.history.replaceState({}, '', '/billing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [creatingProjectInvoiceId, setCreatingProjectInvoiceId] = useState<number | null>(null);
  const createProjectInvoice = useMutation(async ({ projectId, customerId }: { projectId: number; customerId: number }) => {
    setCreatingProjectInvoiceId(projectId);
    const r = await api.post('/invoices', { customer_id: customerId, project_id: projectId, invoice_type: 'project' });
    return r.data;
  }, {
    onSuccess: (data: any) => {
      qc.invalidateQueries('billing-unbilled');
      qc.invalidateQueries('billing-dashboard');
      qc.invalidateQueries('invoices');
      qc.invalidateQueries('review-claims');
      toast.success(data.message || 'Invoice created');
      setCreatingProjectInvoiceId(null);
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Failed to create invoice');
      setCreatingProjectInvoiceId(null);
    },
  });

  const generateMonthly = useMutation(async (body: { period_start: string; period_end: string }) => {
    const r = await api.post('/billing/generate-monthly', body);
    return r.data;
  }, {
    onSuccess: (data: any) => {
      qc.invalidateQueries('billing-dashboard');
      qc.invalidateQueries('billing-unbilled');
      qc.invalidateQueries('invoices');
      qc.invalidateQueries('billing-review');   // the new drafts show on Review & send right away
      qc.invalidateQueries('review-claims');    // a waiting Google-review $10 lands on the customer's new bill
      toast.success(data.message || 'Done');
      setShowGenerate(false);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed'); },
  });

  const prev = (() => {
    const now = new Date();
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0);
    const p = (n: number) => String(n).padStart(2, '0');
    const f = (d: Date) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    return { start: f(s), end: f(e) };
  })();
  const [genStart, setGenStart] = useState(prev.start);
  const [genEnd, setGenEnd] = useState(prev.end);


  const byCustomer: Record<string, UnbilledItem[]> = {};
  const unbilledShown = unbilled.filter(item => {
    if (!dateFrom && !dateTo) return true;
    const d = dayOf(item.completed_at);
    return !!d && (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
  });
  for (const item of unbilledShown) {
    const key = `${item.customer_id}:${item.customer_name}`;
    if (!byCustomer[key]) byCustomer[key] = [];
    byCustomer[key].push(item);
  }

  return (
    <div className="space-y-5">
      {/* Stripe test mode banner */}
      {isStripeTestMode && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-300 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Stripe Test Mode Active</p>
            <p className="text-xs text-amber-700 mt-0.5">Payments do not move real money. Do not send these invoice links to real customers. Switch to live keys when ready to go live.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Billing</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {tab === 'invoices' ? `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}` : `${unbilledCount} uninvoiced item${unbilledCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={async () => {
              setSheetsExporting(true);
              setSheetsMsg(null);
              try {
                const res = await api.post('/integrations/sheets/export/invoices');
                setSheetsMsg(res.data.message);
              } catch (err: any) {
                setSheetsMsg(err.response?.data?.message || 'Export failed');
              } finally {
                setSheetsExporting(false);
              }
            }}
            disabled={sheetsExporting}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg px-3 py-2 bg-white disabled:opacity-50"
          >
            <UploadCloud className="h-4 w-4" /> {sheetsExporting ? 'Exporting…' : '→ Sheets'}
          </button>
          {tab === 'invoices' && draftCnt > 0 && (
            <button onClick={() => setReconcileOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:border-brand-400">
              <Receipt className="h-4 w-4" /> Reconcile with Jobber
            </button>
          )}
          {tab === 'invoices' && (
            <button onClick={() => setGiftOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:border-brand-400">
              <Receipt className="h-4 w-4" /> Gift cards
            </button>
          )}
          {tab === 'invoices' && (
            <button onClick={() => setCreateOpen(true)}
              className="press inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-black text-sm font-medium rounded-xl hover:bg-brand-500 shadow-sm">
              <Plus className="h-4 w-4" /> Create Invoice
            </button>
          )}
          {tab === 'unbilled' && (
            <button onClick={() => setShowGenerate(true)}
              className="press inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-black text-sm font-medium rounded-xl hover:bg-brand-500 shadow-sm">
              <Receipt className="h-4 w-4" /> Generate Monthly
            </button>
          )}
        </div>
      </div>
      {sheetsMsg && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
          {sheetsMsg}
        </div>
      )}

      {/* Stats — this month's money first (owner, Oct 26 2026: "cleanup billing") */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { label: 'Outstanding', val: fmtMoneyK(sentAmt + overAmt), icon: AlertCircle, color: 'bg-blue-500' },
          { label: 'Collected', val: fmtMoneyK(paidAmt), icon: CheckCircle, color: 'bg-green-500' },
          // Tips are split by the crew on the job and paid through Crew Pay; a tip still waiting for a crew says so.
          { label: 'Tips', val: fmtMoneyK(Number(dashData?.invoices?.tips ?? dashData?.tips ?? 0)), icon: DollarSign, color: 'bg-emerald-500',
            sub: Number(dashData?.invoices?.tips ?? 0) > 0 ? (Number(dashData?.invoices?.tips_waiting ?? 0) > 0 ? `$${Number(dashData.invoices.tips_waiting).toFixed(2)} waiting for a crew` : 'split by the crew · in Crew Pay') : undefined },
          { label: 'Drafts', val: String(draftCnt), icon: FileText, color: 'bg-brand-500' },
          { label: 'Uninvoiced', val: String(unbilledCount), icon: Clock, color: 'bg-amber-500' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg shadow p-4 flex items-start gap-3">
            <div className={`rounded-lg p-2 ${s.color}`}><s.icon className="h-4 w-4 text-white" /></div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-xl font-bold text-gray-900">{s.val}</p>
              {(s as any).sub && <p className="text-[11px] text-gray-500 leading-tight mt-0.5">{(s as any).sub}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* The helpers, each folded to one line (owner, Oct 26 2026: "the reminder texts should be collapsed, same with
          the make-the-mow-free feature"); the review nudge lives on Needs Attention and lands here with the card open. */}
      {/* Month-end bills wait for the owner (Oct 1 2026: "review first") — he checks them and taps Send. */}
      <ReviewSendCard onOpen={openInvoice} wanted={reviewWanted} />
      {/* Every bill still owed, nudged in one go after a look (owner, Oct 2 2026: "send follow ups on all billing"). */}
      <FollowUpsCard onOpen={openInvoice} />
      {/* A mow too soon after the last one is free (owner, Sep 30 2026) — the ones already on a bill. */}
      <TooSoonCard />
      {/* Late fees: what the daily job does, and Run now (owner, Nov 1 2026). */}
      <LateFeesCard />
      {/* Yards billing something other than what their last bid promised (owner, Nov 1 2026) — a report, nothing changes. */}
      <PriceCheckCard />
      {/* Who pays by card automatically (owner, Oct 2 2026: "tell me who has auto pay"). */}
      <AutopayCard />

      {/* Owed · paid but not in the bank yet · landed (owner, Nov 1 2026: "the people that have paid but I don't have a payout for yet"). */}
      <MoneyStatusCard />
      <PayoutsCard />
      {/* A month paid outside the app (owner, Sep 28 2026: August was paid through Jobber). */}
      <PaidOutsideCard />

      {/* Tabs — the bills themselves */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-4">
          {(['invoices', 'unbilled'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t === 'invoices' ? 'Invoices' : `Uninvoiced${unbilledCount > 0 ? ` (${unbilledCount})` : ''}`}
            </button>
          ))}
        </nav>
      </div>

      {/* Dates — the same range on both tabs (owner, Oct 2 2026) */}
      <div className="flex gap-2 flex-wrap items-center">
        <select value={datePick} onChange={e => pickDates(e.target.value as DatePick)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
          <option value="all">All dates</option>
          <option value="this_month">This month</option>
          <option value="last_month">Last month</option>
          <option value="last_3">Last 3 months</option>
          <option value="this_year">This year</option>
          <option value="custom">Pick dates…</option>
        </select>
        {datePick !== 'all' && (
          <>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePick('custom'); }} aria-label="From"
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400" />
            <span className="text-xs text-gray-500">to</span>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePick('custom'); }} aria-label="To"
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </>
        )}
        {tab === 'invoices' && datePick !== 'all' && (
          <select value={dateBy} onChange={e => setDateBy(e.target.value as DateBy)} aria-label="Which date"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
            <option value="period">By the month it covers</option>
            <option value="made">By the day it was made</option>
            <option value="paid">By the day it was paid</option>
          </select>
        )}
        {datePick !== 'all' && (
          <button type="button" onClick={() => pickDates('all')} className="text-xs text-gray-600 underline underline-offset-2">Clear dates</button>
        )}
      </div>

      {/* Invoices tab */}
      {tab === 'invoices' && (
        <>
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-44">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search invoices…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
            </select>
          </div>

          {!invLoading && (search || statusFilter || datePick !== 'all') && (
            <p className="text-xs text-gray-600">Showing {invoices.length} bill{invoices.length === 1 ? '' : 's'} · {fmtMoney(shownTotal)}</p>
          )}
          <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
            {invLoading && <div className="p-8 text-center text-gray-500">Loading…</div>}
            {!invLoading && invoices.length === 0 && (
              <div className="p-10 text-center">
                <FileText className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No invoices. Create one to get started.</p>
              </div>
            )}
            {invoices.map(inv => (
              <button key={inv.id} onClick={() => setDetail(inv)}
                className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-sm text-gray-900">{inv.invoice_number}</span>
                    <span className="text-xs text-gray-500">· {inv.customer_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_BADGE[inv.status] || 'bg-gray-100 text-gray-600'}`}>
                      {inv.status}
                    </span>
                    {inv.invoice_type && inv.invoice_type !== 'dynamic' && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-600">
                        {inv.invoice_type === 'fixed_recurring' ? 'fixed' : inv.invoice_type === 'project' ? 'project' : inv.invoice_type === 'deposit' ? 'deposit' : 'one-time'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {inv.billing_period_start ? `${fmtDate(inv.billing_period_start)} – ${fmtDate(inv.billing_period_end)}` : 'No period'}
                    {' · '}{inv.line_item_count} item{inv.line_item_count !== 1 ? 's' : ''}
                    {inv.due_date ? ` · Due ${fmtDate(inv.due_date)}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-gray-900">{fmtMoney(inv.total)}</p>
                  {Number(inv.tip_amount) > 0 && <p className="text-xs text-emerald-700">+ {fmtMoney(inv.tip_amount)} tip</p>}
                  {inv.paid_at && <p className="text-xs text-green-600">Paid {fmtDate(inv.paid_at)}</p>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Unbilled jobs tab */}
      {tab === 'unbilled' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-5 py-3 border-b border-gray-100">
            <p className="text-sm text-gray-500">
              Completed jobs and projects not yet invoiced. Use "Generate Monthly" to batch-invoice jobs by customer. Projects must be invoiced individually.
            </p>
          </div>
          {unbilledLoading ? (
            <div className="p-8 text-center"><div className="animate-spin h-6 w-6 border-b-2 border-brand-600 rounded-full mx-auto" /></div>
          ) : unbilledShown.length === 0 ? (
            <div className="p-10 text-center">
              <CheckCircle className="h-10 w-10 text-green-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{unbilled.length ? 'Nothing unbilled in these dates.' : 'All completed jobs have been invoiced.'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {Object.entries(byCustomer).map(([key, items]) => {
                const customerName = key.split(':').slice(1).join(':');
                const total = items.reduce((s, i) => s + (i.amount || 0), 0);
                return (
                  <UnbilledGroup key={key} customerName={customerName} items={items} total={total}
                    onCreateProjectInvoice={(projectId, customerId) =>
                      createProjectInvoice.mutate({ projectId, customerId })}
                    creatingProjectId={creatingProjectInvoiceId}
                    onNoBill={(jobId, reason) => noBill.mutate({ jobId, reason })}
                    noBillBusyId={noBill.isLoading ? noBill.variables?.jobId ?? null : null} />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {createOpen && (
        <CreateInvoiceModal customers={customers}
          preset={presetCustomer}
          onClose={() => { setCreateOpen(false); setPresetCustomer(null); }}
          onCreated={() => { qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-review'); qc.invalidateQueries('review-claims'); setCreateOpen(false); setPresetCustomer(null); }} />
      )}

      {/* Reconcile-with-Jobber modal */}
      {reconcileOpen && (
        <ReconcileJobberModal
          onClose={() => setReconcileOpen(false)}
          onDone={() => { qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard'); qc.invalidateQueries('billing-review'); setReconcileOpen(false); }} />
      )}

      {/* Gift cards modal */}
      {giftOpen && <GiftCardsModal onClose={() => setGiftOpen(false)} />}

      {/* Detail modal */}
      {detail && (
        <InvoiceDetailModal invoice={detail}
          onClose={() => { setDetail(null); qc.invalidateQueries('billing-review'); }}
          onStatusChange={(status, visits) => updateStatus.mutate({ id: detail.id, status, visits })}
          onShare={() => { setShareInvoice(detail); setDetail(null); }}
          updating={updateStatus.isLoading} />
      )}

      {/* Share modal */}
      {shareInvoice && (
        <ShareInvoiceModal invoice={shareInvoice}
          smsConfigured={smsStatus?.configured ?? false}
          onClose={() => setShareInvoice(null)} />
      )}

      {/* Generate monthly modal */}
      {showGenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Generate Monthly Invoices</h3>
              <p className="text-xs text-gray-500 mt-0.5">One draft invoice per customer for all unbilled jobs in the period. Nothing is sent — check them on Review &amp; send.</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Period Start</label>
                  <input type="date" value={genStart} onChange={e => setGenStart(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Period End</label>
                  <input type="date" value={genEnd} onChange={e => setGenEnd(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                </div>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Customers already invoiced for this period will be skipped to prevent double billing.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShowGenerate(false)}
                className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => generateMonthly.mutate({ period_start: genStart, period_end: genEnd })}
                disabled={generateMonthly.isLoading}
                className="press px-3 py-2 text-sm font-medium text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
                {generateMonthly.isLoading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Unbilled group row ────────────────────────────────────────────────────────
function UnbilledGroup({ customerName, items, total, onCreateProjectInvoice, creatingProjectId, onNoBill, noBillBusyId }: {
  customerName: string; items: UnbilledItem[]; total: number;
  onCreateProjectInvoice?: (projectId: number, customerId: number) => void;
  creatingProjectId?: number | null;
  onNoBill?: (jobId: number, reason: string) => void;
  noBillBusyId?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const jobCount = items.filter(i => i.source_type === 'job').length;
  const projectCount = items.filter(i => i.source_type === 'project').length;
  const summaryParts = [];
  if (jobCount > 0) summaryParts.push(`${jobCount} mowing job${jobCount !== 1 ? 's' : ''}`);
  if (projectCount > 0) summaryParts.push(`${projectCount} project work`);

  return (
    <div>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="text-sm font-medium text-gray-900">{customerName}</span>
          <span className="text-xs text-gray-500">{summaryParts.join(', ')}</span>
        </div>
        <span className="text-sm font-semibold text-gray-900">{fmtMoney(total)}</span>
      </button>
      {open && (
        <div className="bg-gray-50 divide-y divide-gray-100">
          {items.map(item => (
            <div key={`${item.source_type}-${item.id}`} className="pl-6 pr-4 sm:px-10 py-2.5 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {item.source_type === 'project' && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-600 font-medium flex-shrink-0">Project Work</span>
                  )}
                  <p className="text-sm text-gray-800 truncate">{item.description || item.service_type || 'Service'}</p>
                </div>
                <p className="text-xs text-gray-500">
                  {item.property_address ? item.property_address + ' · ' : ''}
                  {fmtDate(item.completed_at)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3 flex-shrink-0">
                <span className="text-sm font-medium text-gray-900">{fmtMoney(item.amount)}</span>
                {/* Not owed (owner, Oct 1 2026): month-end leaves it alone until it is undone on the visit. */}
                {item.source_type === 'job' && item.job_id && onNoBill && (
                  <button type="button"
                    onClick={() => {
                      const reason = window.prompt(`Don't bill this visit — ${customerName}, ${fmtDate(item.completed_at)} (${fmtMoney(item.amount)})? It won't go on any bill until you undo it on the visit. Why? (optional)`, '');
                      if (reason !== null) onNoBill(item.job_id!, reason.trim());
                    }}
                    disabled={noBillBusyId === item.job_id}
                    className="text-xs font-medium text-gray-600 border border-gray-300 rounded-lg px-2.5 py-1 hover:bg-white disabled:opacity-50 whitespace-nowrap">
                    {noBillBusyId === item.job_id ? 'Saving…' : "Don't bill"}
                  </button>
                )}
                {item.source_type === 'project' && item.project_id && onCreateProjectInvoice && (
                  <button
                    onClick={() => onCreateProjectInvoice(item.project_id!, item.customer_id)}
                    disabled={creatingProjectId === item.project_id}
                    className="text-xs font-medium text-brand-700 border border-brand-300 rounded-lg px-2.5 py-1 hover:bg-brand-50 disabled:opacity-50 whitespace-nowrap"
                  >
                    {creatingProjectId === item.project_id ? 'Creating…' : 'Create Invoice'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create Invoice Modal ──────────────────────────────────────────────────────
type InvoiceType = 'dynamic' | 'one_time' | 'fixed_recurring';

function CreateInvoiceModal({ customers, preset, onClose, onCreated }: {
  customers: Customer[]; preset?: { id: number; name: string } | null; onClose: () => void; onCreated: () => void;
}) {
  const today = businessToday();
  const firstOfMonth = today.slice(0, 8) + '01';
  // Last day of the Austin month: day 0 of the next month, built from the
  // business date so a late-evening session doesn't roll into next month.
  const [y, m] = today.split('-').map(Number);
  const lastOfMonth = `${today.slice(0, 8)}${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;

  const [invoiceType, setInvoiceType] = useState<InvoiceType>('dynamic');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerId, setCustomerId] = useState(preset?.id ? String(preset.id) : '');
  const [customerName, setCustomerName] = useState(preset?.name || '');
  const [showPicker, setShowPicker] = useState(false);
  const [periodStart, setPeriodStart] = useState(firstOfMonth);
  const [periodEnd, setPeriodEnd] = useState(lastOfMonth);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [fixedAmount, setFixedAmount] = useState('');
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const [jobsInitialized, setJobsInitialized] = useState(false);

  // Discount state
  const [discountType, setDiscountType] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');

  // Manual line items for one_time
  const [lineItems, setLineItems] = useState([{ description: '', quantity: '1', unit_price: '', service_date: '' }]);

  // Load settings for default due days
  const { data: settingsData } = useQuery('settings', () => api.get('/settings').then(r => r.data));
  useEffect(() => {
    if (settingsData?.invoice_due_days && !dueDate) {
      const days = parseInt(settingsData.invoice_due_days);
      if (!isNaN(days) && days > 0) {
        // Counted from Austin's today — the UTC date rolls over at 7pm here.
        setDueDate(isoPlusDays(businessToday(), days));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsData]);

  const canFetchJobs = invoiceType === 'dynamic' && !!customerId && !!periodStart && !!periodEnd;

  const { data: jobsData, isLoading: jobsLoading } = useQuery(
    ['uninvoiced-jobs', customerId, periodStart, periodEnd],
    () => api.get(`/invoices/uninvoiced-jobs?customer_id=${customerId}&period_start=${periodStart}&period_end=${periodEnd}`).then(r => r.data.jobs as Job[]),
    { enabled: canFetchJobs, staleTime: 10000 }
  );
  const jobs = React.useMemo<Job[]>(() => jobsData || [], [jobsData]);

  // Initialize all jobs as selected when they load
  useEffect(() => {
    if (jobs.length > 0 && !jobsInitialized) {
      setSelectedJobIds(new Set(jobs.map(j => j.id)));
      setJobsInitialized(true);
    }
  }, [jobs, jobsInitialized]);

  // Reset when customer/period changes
  useEffect(() => { setJobsInitialized(false); setSelectedJobIds(new Set()); }, [customerId, periodStart, periodEnd]);

  function toggleJob(id: number) {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const filteredCustomers = customerSearch.trim()
    ? customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
    : customers;

  const addLineItem = () => setLineItems(prev => [...prev, { description: '', quantity: '1', unit_price: '', service_date: '' }]);
  const removeLineItem = (i: number) => setLineItems(prev => prev.filter((_, idx) => idx !== i));
  const updateLineItem = (i: number, field: string, val: string) =>
    setLineItems(prev => prev.map((li, idx) => idx === i ? { ...li, [field]: val } : li));

  const dynamicTotal = Array.from(selectedJobIds).reduce((s, id) => {
    const j = jobs.find(x => x.id === id);
    return s + (j?.price_snapshot || 0);
  }, 0);

  const oneTimeTotal = lineItems.reduce((s, li) => {
    return s + (Number(li.quantity || 1) * Number(li.unit_price || 0));
  }, 0);

  const baseTotal = invoiceType === 'dynamic' ? dynamicTotal
    : invoiceType === 'fixed_recurring' ? Number(fixedAmount || 0)
    : oneTimeTotal;
  const discAmt = Number(discountAmount || 0);
  const discountedTotal = !discountType || !discAmt ? baseTotal
    : discountType === 'fixed' ? Math.max(0, baseTotal - discAmt)
    : Math.max(0, baseTotal * (1 - discAmt / 100));

  const applyQuickDiscount = (type: string, amount: number, label: string) => {
    if (discountType === type && Number(discountAmount) === amount) {
      setDiscountType(''); setDiscountAmount(''); setDiscountLabel('');
    } else {
      setDiscountType(type); setDiscountAmount(String(amount)); setDiscountLabel(label);
    }
  };

  const create = useMutation(async () => {
    const body: any = {
      customer_id: Number(customerId),
      invoice_type: invoiceType,
      due_date: dueDate || undefined,
      notes: notes || undefined,
      ...(discountType && discAmt > 0 ? { discount_type: discountType, discount_amount: discAmt, discount_label: discountLabel || undefined } : {}),
    };

    if (invoiceType === 'dynamic') {
      body.billing_period_start = periodStart;
      body.billing_period_end = periodEnd;
      body.included_job_ids = Array.from(selectedJobIds);
    } else if (invoiceType === 'fixed_recurring') {
      body.billing_period_start = periodStart;
      body.billing_period_end = periodEnd;
      body.fixed_amount = Number(fixedAmount);
    } else {
      body.line_items = lineItems
        .filter(li => li.description.trim())
        .map(li => ({
          description: li.description,
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
          amount: (Number(li.quantity) || 1) * (Number(li.unit_price) || 0),
          service_date: li.service_date || undefined,   // the day the work was done, when he typed one
        }));
    }

    const r = await api.post('/invoices', body);
    return r.data;
  }, {
    onSuccess: (data: any) => { toast.success(data.message || 'Invoice created'); onCreated(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to create invoice'); },
  });

  const canCreate = !!customerId && (
    (invoiceType === 'dynamic' && selectedJobIds.size > 0) ||
    (invoiceType === 'fixed_recurring' && !!fixedAmount) ||
    (invoiceType === 'one_time' && lineItems.some(li => li.description.trim()))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Create Invoice</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Invoice type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Invoice Type</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                ['dynamic', 'From Jobs', 'Completed jobs in a period'],
                ['one_time', 'One-Time', 'Custom line items'],
                ['fixed_recurring', 'Fixed Monthly', 'Flat rate'],
              ] as [InvoiceType, string, string][]).map(([val, label, desc]) => (
                <button key={val} type="button" onClick={() => setInvoiceType(val)}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${invoiceType === val ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-600 hover:border-brand-300'}`}>
                  <div className="font-medium">{label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Customer picker */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Customer *</label>
            {customerId ? (
              <div className="flex items-center justify-between px-3 py-2 border border-brand-400 rounded-lg bg-brand-50">
                <span className="text-sm font-medium text-brand-900">{customerName}</span>
                <button className="text-xs text-brand-600 hover:text-brand-800 font-medium"
                  onClick={() => { setCustomerId(''); setCustomerName(''); setCustomerSearch(''); setShowPicker(true); }}>
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="Search customers…"
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setShowPicker(true); }}
                  onFocus={() => setShowPicker(true)} />
                {showPicker && filteredCustomers.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredCustomers.slice(0, 20).map(c => (
                      <button key={c.id} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-brand-50"
                        onClick={() => { setCustomerId(String(c.id)); setCustomerName(c.name); setCustomerSearch(''); setShowPicker(false); }}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Period (dynamic + fixed) */}
          {(invoiceType === 'dynamic' || invoiceType === 'fixed_recurring') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Period Start</label>
                <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Period End</label>
                <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
              </div>
            </div>
          )}

          {/* Fixed amount */}
          {invoiceType === 'fixed_recurring' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Monthly Rate ($) *</label>
              <input type="number" step="0.01" value={fixedAmount} onChange={e => setFixedAmount(e.target.value)}
                placeholder="e.g. 120.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </div>
          )}

          {/* Dynamic: job list with checkboxes */}
          {invoiceType === 'dynamic' && canFetchJobs && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Completed Jobs</label>
                {jobs.length > 0 && (
                  <button className="text-xs text-brand-600 hover:text-brand-800"
                    onClick={() => {
                      if (selectedJobIds.size === jobs.length) setSelectedJobIds(new Set());
                      else setSelectedJobIds(new Set(jobs.map(j => j.id)));
                    }}>
                    {selectedJobIds.size === jobs.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                {jobsLoading && <p className="text-xs text-gray-500 px-3 py-2">Loading…</p>}
                {!jobsLoading && jobs.length === 0 && (
                  <p className="text-xs text-gray-500 px-3 py-2 italic">No uninvoiced completed jobs in this period.</p>
                )}
                {jobs.map(j => {
                  const checked = selectedJobIds.has(j.id);
                  const addr = j.property_label || j.address || '';
                  return (
                    <label key={j.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${checked ? 'bg-white' : 'bg-gray-50 opacity-50'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleJob(j.id)}
                        className="h-4 w-4 text-brand-600 rounded border-gray-300" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700">{j.scheduled_date}{j.service_type ? ` · ${j.service_type}` : ''}</p>
                        {addr && <p className="text-xs text-gray-500 truncate">{addr}</p>}
                      </div>
                      <span className="text-xs font-semibold text-gray-900 flex-shrink-0">{fmtMoney(j.price_snapshot)}</span>
                    </label>
                  );
                })}
                {!jobsLoading && jobs.length > 0 && (
                  <div className="px-3 py-2 flex justify-between bg-gray-50">
                    <p className="text-xs font-medium text-gray-600">{selectedJobIds.size} of {jobs.length} selected</p>
                    <p className="text-xs font-bold text-gray-900">{fmtMoney(dynamicTotal)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* One-time: manual line items */}
          {invoiceType === 'one_time' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Line Items</label>
              <div className="space-y-2">
                {lineItems.map((li, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex gap-2 items-start">
                      <input value={li.description} onChange={e => updateLineItem(i, 'description', e.target.value)}
                        placeholder="Description" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 min-w-0" />
                      <input type="number" value={li.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)}
                        placeholder="Qty" className="w-14 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                      <input type="number" step="0.01" value={li.unit_price} onChange={e => updateLineItem(i, 'unit_price', e.target.value)}
                        placeholder="Price" className="w-24 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                      {lineItems.length > 1 && (
                        <button onClick={() => removeLineItem(i)}><X className="h-4 w-4 text-gray-500 hover:text-red-500 mt-2.5" /></button>
                      )}
                    </div>
                    {/* The day the work was done — shows beside the line on the bill; blank is fine for a month or a credit. */}
                    <label className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                      <span>Service date <span className="text-gray-400">(optional)</span></span>
                      <input type="date" value={li.service_date} onChange={e => updateLineItem(i, 'service_date', e.target.value)}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400" />
                    </label>
                  </div>
                ))}
                <button onClick={addLineItem}
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1 mt-1">
                  <Plus className="h-3 w-3" /> Add line item
                </button>
                <div className="text-right text-sm font-bold text-gray-900 pt-1">
                  Total: {fmtMoney(oneTimeTotal)}
                </div>
              </div>
            </div>
          )}

          {/* Discount */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Discount</label>
            <div className="flex gap-2 mb-2">
              <button type="button"
                onClick={() => applyQuickDiscount('fixed', 10, 'Google Review')}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${discountType === 'fixed' && Number(discountAmount) === 10 ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-600 hover:border-brand-300'}`}>
                Google Review −$10
              </button>
              <button type="button"
                onClick={() => applyQuickDiscount('percent', 10, 'Winter Discount')}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${discountType === 'percent' && Number(discountAmount) === 10 ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-600 hover:border-brand-300'}`}>
                Winter −10%
              </button>
            </div>
            <div className="flex gap-2">
              <select value={discountType} onChange={e => setDiscountType(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 w-28">
                <option value="">No discount</option>
                <option value="fixed">Fixed $</option>
                <option value="percent">Percent %</option>
              </select>
              {discountType && (
                <>
                  <input type="number" step="0.01" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)}
                    placeholder={discountType === 'fixed' ? 'Amount' : 'Percent'}
                    className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  <input value={discountLabel} onChange={e => setDiscountLabel(e.target.value)} placeholder="Label (optional)"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                </>
              )}
            </div>
            {discountType && discAmt > 0 && (
              <p className="text-xs text-brand-700 mt-1.5 font-medium">
                Total after discount: <span className="font-bold">${discountedTotal.toFixed(2)}</span>
                {' '}(was ${baseTotal.toFixed(2)})
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-1">
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={() => create.mutate()} disabled={create.isLoading || !canCreate}
              className="press px-4 py-2 text-sm font-medium text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
              {create.isLoading ? 'Creating…' : 'Create Draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Invoice Detail Modal ──────────────────────────────────────────────────────
function InvoiceDetailModal({ invoice, onClose, onStatusChange, onShare, updating }: {
  invoice: Invoice; onClose: () => void;
  onStatusChange: (s: string, visits?: VoidVisits) => void;
  onShare: () => void;
  updating: boolean;
}) {
  const qc = useQueryClient();
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', quantity: '1', unit_price: '', service_date: '' });
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [payAmount, setPayAmount] = useState(String(invoice.total || ''));
  const [payTip, setPayTip] = useState('');   // a cash / check tip written down with the payment
  const [payMethod, setPayMethod] = useState('check');
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discType, setDiscType] = useState<'fixed' | 'percent'>('fixed');
  const [discAmount, setDiscAmount] = useState('');
  const [discLabel, setDiscLabel] = useState('');

  const { data, refetch } = useQuery(['invoice-detail', invoice.id],
    () => api.get(`/invoices/${invoice.id}`).then(r => r.data)
  );
  const lineItems: LineItem[] = data?.lineItems || [];
  const payments: any[] = data?.payments || [];
  const liveInv = data?.invoice || invoice;
  const liveTotal: number = liveInv.total ?? invoice.total;
  const canEdit = invoice.status === 'draft' || invoice.status === 'sent';
  // Void asks about the visits on the bill (owner, Oct 1 2026) — a voided bill's visits otherwise bill again next month.
  const [voidAsk, setVoidAsk] = useState(false);
  const visitCount = new Set(lineItems.filter(l => l.job_id).map(l => l.job_id)).size;
  // Until the bill's lines have loaded nobody knows whether it has visits — so Void asks anyway.
  const linesKnown = !!data;

  // The standing offers, one tap each. "Referral" takes a code in the label so
  // the invoice says who sent them.
  const PRESETS: { label: string; type: 'fixed' | 'percent'; amount: number; hint?: string }[] = [
    { label: 'Military', type: 'percent', amount: 10 },
    { label: 'Google review', type: 'fixed', amount: 10, hint: 'after you see the review' },
    { label: 'Referral', type: 'fixed', amount: 10, hint: 'add their name in the label' },
    { label: 'Winter booking', type: 'percent', amount: 10 },
  ];

  const setDiscount = useMutation(
    (v: { discount_type: string | null; discount_amount?: number; discount_label?: string }) =>
      api.put(`/invoices/${invoice.id}/discount`, v),
    {
      onSuccess: (r: any) => {
        toast.success(r.data?.message || 'Discount updated');
        setDiscountOpen(false); setDiscAmount(''); setDiscLabel('');
        refetch(); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing'); qc.invalidateQueries('review-claims');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not change the discount'); },
    },
  );

  const addItem = useMutation(async () => {
    const amount = Number(newItem.quantity || 1) * Number(newItem.unit_price || 0);
    await api.post(`/invoices/${invoice.id}/line-items`, {
      description: newItem.description,
      quantity: Number(newItem.quantity || 1),
      unit_price: Number(newItem.unit_price || 0),
      amount,
      service_date: newItem.service_date || null,   // the day the work was done, when he typed one
    });
  }, {
    onSuccess: () => {
      refetch(); qc.invalidateQueries('invoices');
      setAddingItem(false); setNewItem({ description: '', quantity: '1', unit_price: '', service_date: '' });
      toast.success('Item added');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to add item'); },
  });

  const saveEdit = useMutation(async (item: any) => {
    await api.put(`/invoices/line-items/${item.id}`, {
      description: item.description,
      quantity: Number(item.quantity || 1),
      unit_price: Number(item.unit_price || 0),
      amount: Number(item.quantity || 1) * Number(item.unit_price || 0),
      service_date: dayInput(item.service_date) || null,   // blank clears the day
    });
  }, {
    onSuccess: () => { refetch(); qc.invalidateQueries('invoices'); setEditingItem(null); toast.success('Updated'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to update'); },
  });

  // "Charge card on file" — the customer's card at Stripe pays what is owed on this bill, now
  // (owner, Oct 2 2026). Only shown when a card is there; the server says in plain words why a
  // charge did not go through (services/autopay.declineWords).
  const chargeable = ['sent', 'overdue'].includes(invoice.status);
  const { data: pay } = useQuery(['customer-payment', invoice.customer_id],
    () => api.get(`/billing/customers/${invoice.customer_id}/payment`).then(r => r.data),
    { enabled: chargeable && !!invoice.customer_id, retry: false });
  const chargeCard = useMutation(() => api.post(`/billing/invoices/${invoice.id}/charge-card`).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Charged');
      refetch(); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard'); qc.invalidateQueries('attention');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not charge that card', { autoClose: 9000 }); },
  });
  const paidSoFar = payments.reduce((t: number, p: any) => t + (Number(p.amount) || 0), 0);
  const owed = Math.max(0, Math.round((liveTotal - paidSoFar) * 100) / 100);
  // The server refuses a charge for a customer whose autopay is off (they chose to pay by hand) and when
  // nothing charges at all (practice · no Stripe · the Settings switch) — so the button says so first.
  const cardShown = chargeable && !!pay?.card?.on_file && owed > 0;
  const noCharge = pay ? noChargeWords(pay) : null;
  const chargeHint = !pay ? null
    : !pay.autopay_enabled ? { words: 'Card on file, but their autopay is off — they chose to pay by hand. Turn it on from their customer card if they ask.', settings: false }
    : noCharge ? { words: `Card on file, but nothing charges from here: ${noCharge.words.charAt(0).toLowerCase()}${noCharge.words.slice(1)}`, settings: noCharge.settings }
    : null;

  // Waive a late fee or interest line (services/lateFees): $0, logged with who and why.
  const waiveFee = useMutation(async ({ id, reason }: { id: number; reason: string }) => (await api.post(`/invoices/line-items/${id}/waive`, { reason })).data, {
    onSuccess: (d: any) => { refetch(); qc.invalidateQueries('invoices'); toast.success(d?.message || 'Waived'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not waive that'); },
  });
  const deleteItem = useMutation(async (itemId: number) => {
    await api.delete(`/invoices/line-items/${itemId}`);
  }, {
    onSuccess: () => { refetch(); qc.invalidateQueries('invoices'); toast.success('Removed'); },
    onError: () => { toast.error('Failed to remove'); },
  });

  const recordPayment = useMutation(async () => {
    const { data } = await api.post(`/invoices/${invoice.id}/payments`, {
      amount: Number(payAmount),
      method: payMethod,
      ...(Number(payTip) > 0 ? { tip_amount: Number(payTip) } : {}),
    });
    return data;
  }, {
    onSuccess: (data: any) => {
      refetch(); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard');
      setRecordingPayment(false);
      toast.success(data?.message || 'Payment recorded');
      onClose();
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed'); },
  });

  const [giftCode, setGiftCode] = useState('');
  const redeemGift = useMutation(
    () => api.post('/gift-cards/redeem', { code: giftCode.trim(), invoice_id: invoice.id }).then(r => r.data),
    {
      onSuccess: (data: any) => { toast.success(data?.message || 'Applied'); setGiftCode(''); refetch(); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not apply that card'); },
    });

  const followUp = useMutation(async () => {
    const { data } = await api.post(`/invoices/${invoice.id}/follow-up`);
    return data;
  }, {
    onSuccess: (data: any) => {
      if (data?.sent) { toast.success(data.message || 'Reminder sent'); }
      else if (data?.draft) {
        // SMS not set up (or opted out) — show the message for Michael to send.
        toast.info(data.message || 'Copy this reminder');
        window.prompt('Copy this reminder to text them:', data.draft.body);
      } else { toast.info(data?.message || 'Done'); }
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send reminder'); },
  });

  const deleteInvoice = useMutation(async () => {
    const r = await api.delete(`/invoices/${invoice.id}`);
    return r.data;
  }, {
    // The server says what happened to a Google review claim that was on the bill.
    onSuccess: (d: any) => { qc.invalidateQueries('invoices'); qc.invalidateQueries('review-claims'); toast.success(d?.message || 'Invoice deleted'); onClose(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Cannot delete'); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{invoice.invoice_number}</h3>
            <p className="text-xs text-gray-500">{invoice.customer_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {invoice.status !== 'void' && invoice.status !== 'paid' && (
              <button onClick={onShare} className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5">
                <Send className="h-4 w-4" /> {invoice.status === 'draft' ? 'Send invoice' : 'Send again'}
              </button>
            )}
            <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Meta */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm px-2 py-0.5 rounded-full font-semibold ${STATUS_BADGE[invoice.status] || 'bg-gray-100'}`}>
              {invoice.status}
            </span>
            {invoice.invoice_type && invoice.invoice_type !== 'dynamic' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600">
                {invoice.invoice_type === 'fixed_recurring' ? 'fixed monthly' : invoice.invoice_type === 'deposit' ? 'deposit' : 'one-time'}
              </span>
            )}
            {invoice.billing_period_start && (
              <span className="text-sm text-gray-500">
                {fmtDate(invoice.billing_period_start)} – {fmtDate(invoice.billing_period_end)}
              </span>
            )}
            {invoice.due_date && <span className="text-sm text-gray-500">Due {fmtDate(invoice.due_date)}</span>}
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Line Items ({lineItems.length})</p>
              {canEdit && !addingItem && (
                <button onClick={() => setAddingItem(true)}
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1">
                  <Plus className="h-3 w-3" /> Add item
                </button>
              )}
            </div>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
              {lineItems.length === 0 && !addingItem && (
                <p className="text-sm text-gray-500 italic px-3 py-3">No line items. Add one or recreate invoice.</p>
              )}
              {lineItems.map((li: any) => (
                editingItem?.id === li.id ? (
                  <div key={li.id} className="px-3 py-2 space-y-2 bg-brand-50">
                    <input value={editingItem.description}
                      onChange={e => setEditingItem((x: any) => ({ ...x, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                    <div className="flex gap-2">
                      <input type="number" value={editingItem.quantity}
                        onChange={e => setEditingItem((x: any) => ({ ...x, quantity: e.target.value }))}
                        className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Qty" />
                      <input type="number" step="0.01" value={editingItem.unit_price}
                        onChange={e => setEditingItem((x: any) => ({ ...x, unit_price: e.target.value }))}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Unit price" />
                      <span className="text-sm text-gray-500 self-center text-right min-w-16">
                        = {fmtMoney(Number(editingItem.quantity || 1) * Number(editingItem.unit_price || 0))}
                      </span>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="w-20 flex-shrink-0">Service date</span>
                      <input type="date" value={dayInput(editingItem.service_date)}
                        onChange={e => setEditingItem((x: any) => ({ ...x, service_date: e.target.value }))}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400" />
                      {dayInput(editingItem.service_date) && (
                        <button type="button" onClick={() => setEditingItem((x: any) => ({ ...x, service_date: '' }))} className="text-gray-500 underline">clear</button>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit.mutate(editingItem)} disabled={saveEdit.isLoading}
                        className="press px-3 py-1 text-xs font-medium text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">Save</button>
                      <button onClick={() => setEditingItem(null)}
                        className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div key={li.id} className="px-3 py-2 flex items-center justify-between group">
                    {/* The day the work was done, then the words; a month line or a credit has no day. */}
                    <span className="w-12 flex-shrink-0 text-xs text-gray-500 tabular-nums">{fmtDay(li.service_date) || '—'}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${li.waived_at ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{li.description}</p>
                      {li.quantity != null && li.quantity !== 1 && li.unit_price != null && (
                        <p className="text-xs text-gray-500">{li.quantity} × {fmtMoney(li.unit_price)}</p>
                      )}
                      {/* A late fee or interest line (services/lateFees): waived stays as $0 so it never comes back. */}
                      {li.fee_kind && <p className="text-[11px] text-amber-700">{li.waived_at ? `Waived${li.waived_reason ? ` — ${li.waived_reason}` : ''}` : li.fee_kind === 'interest' ? 'Interest, added by the daily late-fee run' : 'Late fee, added by the daily late-fee run'}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">{fmtMoney(li.amount)}</p>
                      {li.fee_kind && !li.waived_at && invoice.status !== 'paid' && (
                        <button type="button" onClick={() => { const reason = window.prompt(`Waive this ${li.fee_kind === 'interest' ? 'interest' : 'late fee'} (${fmtMoney(li.amount)})? Say why — it is logged.`); if (reason !== null) waiveFee.mutate({ id: li.id, reason: reason.trim() }); }}
                          disabled={waiveFee.isLoading} className="text-xs font-medium text-amber-800 border border-amber-300 rounded-lg px-2 py-0.5 hover:bg-amber-50 disabled:opacity-50">Waive</button>
                      )}
                      {canEdit && !li.fee_kind && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditingItem({ ...li, unit_price: li.unit_price ?? li.amount })}
                            className="p-1 text-gray-500 hover:text-brand-600 rounded">
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => { if (window.confirm('Remove line item?')) deleteItem.mutate(li.id); }}
                            className="p-1 text-gray-500 hover:text-red-500 rounded">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              ))}

              {addingItem && (
                <div className="px-3 py-2 space-y-2 bg-brand-50">
                  <input value={newItem.description} onChange={e => setNewItem(x => ({ ...x, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="Description (e.g. Fertilizer treatment)" />
                  <div className="flex gap-2">
                    <input type="number" value={newItem.quantity} onChange={e => setNewItem(x => ({ ...x, quantity: e.target.value }))}
                      className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Qty" />
                    <input type="number" step="0.01" value={newItem.unit_price} onChange={e => setNewItem(x => ({ ...x, unit_price: e.target.value }))}
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Unit price" />
                  </div>
                  <label className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <span>Service date <span className="text-gray-400">(optional)</span></span>
                    <input type="date" value={newItem.service_date} onChange={e => setNewItem(x => ({ ...x, service_date: e.target.value }))}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => addItem.mutate()} disabled={addItem.isLoading || !newItem.description}
                      className="press px-3 py-1 text-xs font-medium text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">Add</button>
                    <button onClick={() => setAddingItem(false)}
                      className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              )}

              {liveInv.discount_type && (liveInv.discount_amount ?? 0) > 0 && (
                <>
                  <div className="px-3 py-2 flex items-center justify-between bg-gray-50 border-t border-gray-100">
                    <p className="text-sm text-gray-500">Subtotal</p>
                    <p className="text-sm text-gray-700">{fmtMoney(liveInv.subtotal)}</p>
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between bg-brand-50 border-t border-brand-100">
                    <p className="text-sm text-brand-700">
                      {liveInv.discount_label || 'Discount'}
                      {' '}({liveInv.discount_type === 'percent' ? `${liveInv.discount_amount}%` : `$${(liveInv.discount_amount ?? 0).toFixed(2)} off`})
                    </p>
                    <p className="text-sm font-medium text-brand-700">
                      −{fmtMoney(liveInv.subtotal - liveTotal)}
                    </p>
                  </div>
                </>
              )}
              <div className="px-3 py-2 flex items-center justify-between bg-gray-50 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-900">Total</p>
                <p className="text-base font-bold text-gray-900">{fmtMoney(liveTotal)}</p>
              </div>

              {/* Discounts — changeable until the invoice is paid */}
              {canEdit && (
                <div className="px-3 py-2 border-t border-gray-200">
                  {!discountOpen ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-500">Discount:</span>
                      <UploadReviewScreenshot invoiceId={invoice.id} onDone={() => refetch()} />
                      {PRESETS.map(p => (
                        <button key={p.label}
                          title={p.hint}
                          onClick={() => setDiscount.mutate({ discount_type: p.type, discount_amount: p.amount, discount_label: p.label })}
                          disabled={setDiscount.isLoading}
                          className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-700 hover:border-brand-400 hover:text-brand-700 disabled:opacity-50">
                          {p.label} {p.type === 'percent' ? `${p.amount}%` : `$${p.amount}`}
                        </button>
                      ))}
                      <button onClick={() => { setDiscountOpen(true); setDiscType((liveInv.discount_type as any) || 'fixed'); setDiscAmount(String(liveInv.discount_amount || '')); setDiscLabel(liveInv.discount_label || ''); }}
                        className="text-xs px-2.5 py-1 rounded-full border border-dashed border-gray-300 text-gray-600">Custom…</button>
                      {liveInv.discount_type && (
                        <button onClick={() => setDiscount.mutate({ discount_type: null })} disabled={setDiscount.isLoading}
                          className="text-xs text-gray-500 hover:text-red-600 ml-auto">Remove</button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        <select value={discType} onChange={e => setDiscType(e.target.value as any)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                          <option value="fixed">$ off</option>
                          <option value="percent">% off</option>
                        </select>
                        <input type="number" step="0.01" value={discAmount} onChange={e => setDiscAmount(e.target.value)}
                          placeholder={discType === 'percent' ? '10' : '10.00'}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-24" />
                        <input value={discLabel} onChange={e => setDiscLabel(e.target.value)}
                          placeholder="Reason on the invoice — e.g. Referral — the Kleins"
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-[12rem]" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setDiscountOpen(false)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Cancel</button>
                        <button onClick={() => setDiscount.mutate({ discount_type: discType, discount_amount: Number(discAmount), discount_label: discLabel || undefined })}
                          disabled={setDiscount.isLoading || !Number(discAmount)}
                          className="press text-xs px-3 py-1.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
                          {setDiscount.isLoading ? 'Applying…' : 'Apply discount'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Payments recorded */}
          {payments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Payments Recorded</p>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                {payments.map((p: any) => (
                  <div key={p.id} className="px-3 py-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-700">{fmtDate(p.paid_at)} · {p.method === 'stripe' ? 'Stripe (card)' : p.method}</p>
                      <p className="text-sm font-semibold text-green-700">{fmtMoney(p.amount)}</p>
                    </div>
                    {p.stripe_session_id && (
                      <p className="text-xs text-gray-500 mt-0.5 font-mono truncate">ref: {p.stripe_session_id}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Record payment form */}
          {recordingPayment && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
              <p className="text-xs font-medium text-green-800">Record Payment</p>
              <div className="flex gap-2">
                <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="Amount" />
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="venmo">Venmo</option>
                  <option value="zelle">Zelle</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" step="0.01" min="0" value={payTip} onChange={e => setPayTip(e.target.value)}
                  className="w-28 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="Tip (optional)" />
                <span className="text-xs text-gray-500">on top of the bill, if they tipped in cash or on the check</span>
              </div>
              <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded px-2 py-1.5">
                Card payments collected via Stripe will mark paid automatically via webhook, tip included.
              </div>
              <div className="flex gap-2">
                <button onClick={() => recordPayment.mutate()} disabled={recordPayment.isLoading || !payAmount}
                  className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                  Record
                </button>
                <button onClick={() => setRecordingPayment(false)}
                  className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          )}

          {/* Actions — recording the payment is the job; the rest is housekeeping */}
          {!['paid', 'void'].includes(invoice.status) && !recordingPayment && (
            <button onClick={() => setRecordingPayment(true)}
              className="w-full py-3 text-base font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 flex items-center justify-center gap-2">
              <Check className="h-5 w-5" /> Record payment
            </button>
          )}
          <div className="flex gap-3 flex-wrap items-center pt-2 border-t border-gray-100">
            {invoice.status === 'draft' && (
              <button onClick={() => onStatusChange('sent')} disabled={updating}
                className="px-2 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50">
                Mark sent
              </button>
            )}
            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
              <button onClick={() => followUp.mutate()} disabled={followUp.isLoading}
                className="px-2 py-1 text-xs font-medium text-amber-700 border border-amber-200 rounded-md hover:bg-amber-50 disabled:opacity-50">
                {followUp.isLoading ? 'Sending…' : 'Send reminder'}
              </button>
            )}
            {cardShown && (
              <button
                onClick={() => { if (window.confirm(`Charge ${fmtMoney(owed)} to ${invoice.customer_name}'s card on file (${pay.card.brand || 'card'} ${pay.card.last4 || ''})?`)) chargeCard.mutate(); }}
                disabled={chargeCard.isLoading || !!chargeHint}
                title={chargeHint?.words}
                className="px-2 py-1 text-xs font-medium text-green-700 border border-green-200 rounded-md hover:bg-green-50 disabled:opacity-50">
                {chargeCard.isLoading ? 'Charging…' : 'Charge card on file'}
              </button>
            )}
            {!['void'].includes(invoice.status) && (
              <button onClick={() => { if (!linesKnown || visitCount > 0) setVoidAsk(true); else if (window.confirm('Void this invoice?')) onStatusChange('void'); }}
                disabled={updating}
                className="px-2 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50">
                Void
              </button>
            )}
            {invoice.status === 'draft' && (
              <button onClick={() => { if (window.confirm('Delete this draft?')) deleteInvoice.mutate(); }}
                className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-red-500">
                Delete
              </button>
            )}
          </div>

          {cardShown && chargeHint && (
            <p className="text-xs text-amber-800 -mt-1">
              {chargeHint.words}{chargeHint.settings && <> <RouterLink to="/settings#billing-automation" className="underline font-semibold">Settings → Billing</RouterLink></>}
            </p>
          )}

          {voidAsk && invoice.status !== 'void' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
              <p className="text-sm font-semibold text-gray-900">Void {invoice.invoice_number}. What about its visits?</p>
              <p className="text-xs text-gray-600">
                {linesKnown ? `${visitCount} visit${visitCount === 1 ? '' : 's'} on this bill.` : 'Its lines are still loading.'} A visit that is also on another bill is left alone.
              </p>
              <p className="text-xs text-gray-600">
                Free is the visit only — it shows on the next bill at $0, and its add-ons and travel still bill. Don't bill keeps all of it off.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button type="button" disabled={updating} onClick={() => onStatusChange('void', 'rebill')}
                  className="px-3 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left">
                  Bill them on the next bill
                </button>
                <button type="button" disabled={updating} onClick={() => onStatusChange('void', 'no_bill')}
                  className="px-3 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left">
                  Don't bill them (not owed)
                </button>
                <button type="button" disabled={updating} onClick={() => onStatusChange('void', 'free')}
                  className="px-3 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left">
                  Make them free
                </button>
                <button type="button" onClick={() => setVoidAsk(false)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 text-left">
                  Cancel — keep the bill
                </button>
              </div>
            </div>
          )}

          {!['paid', 'void'].includes(invoice.status) && (
            <div className="flex items-center gap-2 pt-1">
              <input value={giftCode} onChange={e => setGiftCode(e.target.value)} placeholder="Gift card code"
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40 font-mono" />
              <button onClick={() => redeemGift.mutate()} disabled={redeemGift.isLoading || !giftCode.trim()}
                className="text-sm font-medium text-brand-700 border border-brand-200 rounded-lg px-3 py-1.5 hover:bg-brand-50 disabled:opacity-50">
                {redeemGift.isLoading ? 'Applying…' : 'Apply gift card'}
              </button>
            </div>
          )}

          {(invoice.status === 'paid' || invoice.status === 'void') && (
            <p className="text-sm text-gray-500 italic">
              {invoice.status === 'paid' ? `Paid ${fmtDate(invoice.paid_at)}${Number(liveInv.tip_amount ?? invoice.tip_amount) > 0 ? ` · tip ${fmtMoney(Number(liveInv.tip_amount ?? invoice.tip_amount))}` : ''}` : 'Voided — no further actions available'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Share Invoice Modal ───────────────────────────────────────────────────────
function ShareInvoiceModal({ invoice, onClose, smsConfigured }: { invoice: Invoice; onClose: () => void; smsConfigured?: boolean }) {
  const qc = useQueryClient();
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<'link' | 'msg' | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsMsg, setSmsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  async function sendViaSms() {
    setSmsSending(true);
    setSmsMsg(null);
    try {
      const res = await api.post(`/invoices/${invoice.id}/send-sms`);
      setSmsMsg({ type: 'success', text: res.data.message || 'Invoice SMS sent!' });
      qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard');
    } catch (err: any) {
      setSmsMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send SMS.' });
    } finally {
      setSmsSending(false);
    }
  }

  // null until the tick list loads; '' when every address is unticked.
  const [emailPick, setEmailPick] = useState<string | null>(null);
  // Nobody on file: the picker shows a box to type one, and the send saves it to their card.
  const [emailMissing, setEmailMissing] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  async function sendViaEmail() {
    setEmailSending(true);
    setEmailMsg(null);
    try {
      if (emailMissing && !looksLikeEmail(emailPick || '')) {
        setEmailMsg({ type: 'error', text: `${invoice.customer_name || 'They'} ha${invoice.customer_name ? 's' : 've'} no email on file — type it in the box below, then tap Send via Email. It is saved to their client card.` });
        return;
      }
      if (emailPick === '') { setEmailMsg({ type: 'error', text: 'Tick at least one email.' }); return; }
      const res = await api.post(`/invoices/${invoice.id}/send-email`, emailPick ? { to: emailPick, ...(emailMissing ? { save_email: true } : {}) } : {});
      setEmailMsg({ type: 'success', text: res.data.message || 'Invoice emailed!' });
      // Their card has it now: the box turns back into the usual list.
      if (emailMissing) { setPickerKey(k => k + 1); qc.invalidateQueries('customers'); }
      qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard');
    } catch (err: any) {
      setEmailMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send email.' });
    } finally {
      setEmailSending(false);
    }
  }

  // A draft or $0 invoice has no customer link yet — say so where the link
  // would be, instead of a red toast every time the invoice is opened
  // (owner, Sep 18 2026: "why does it say fail to generate share link?").
  const [shareNote, setShareNote] = useState<string | null>(null);
  useEffect(() => {
    setShareNote(null);
    api.post(`/invoices/${invoice.id}/share-token`)
      .then(r => { setShareToken(r.data.share_token); setLoading(false); if (r.data.marked_sent) { toast.success(r.data.message || 'Marked sent — the link works now'); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard'); } })
      .catch((e: any) => { setShareNote(e?.response?.data?.message || 'No link yet'); setLoading(false); });
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [invoice.id, qc]);

  const shareUrl = shareToken ? `${window.location.origin}/invoice/${shareToken}` : '';
  const message = shareToken
    ? `Hi ${invoice.customer_name}, here is your invoice ${invoice.invoice_number} for ${fmtMoney(invoice.total)}.\n\nView it here: ${shareUrl}`
    : '';

  function copyText(text: string, type: 'link' | 'msg') {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      toast.success('Copied!');
      timerRef.current = setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Share Invoice</h3>
            <p className="text-xs text-gray-500">{invoice.invoice_number} · {invoice.customer_name} · {fmtMoney(invoice.total)}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-4"><div className="animate-spin h-6 w-6 border-b-2 border-brand-600 rounded-full" /></div>
          ) : (
            <>
              {/* Share link */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Invoice Link</label>
                {shareNote && !shareToken && (
                  <p className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{shareNote}</p>
                )}
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 min-w-0">
                    <Link className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-600 truncate">{shareUrl || 'No link until the invoice is sent'}</span>
                  </div>
                  <button onClick={() => copyText(shareUrl, 'link')}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${copied === 'link' ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {copied === 'link' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied === 'link' ? 'Copied' : 'Copy'}
                  </button>
                  <a href={shareUrl} target="_blank" rel="noreferrer"
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>

              {/* Message to send */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Message to Customer</label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {message}
                </div>
                <button onClick={() => copyText(message, 'msg')}
                  className={`mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${copied === 'msg' ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {copied === 'msg' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === 'msg' ? 'Copied' : 'Copy message'}
                </button>
              </div>

              {emailMsg && (
                <div className={`rounded-md px-3 py-2 text-sm ${emailMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                  {emailMsg.text}
                </div>
              )}
              {smsMsg && (
                <div className={`rounded-md px-3 py-2 text-sm ${smsMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                  {smsMsg.text}
                </div>
              )}
              <div className="px-0"><EmailPicker key={pickerKey} url={`/invoices/${invoice.id}/email-to`} onChange={setEmailPick} onMissing={setEmailMissing} who={invoice.customer_name} /></div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={sendViaEmail}
                  disabled={emailSending}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-brand-300 rounded-lg text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-50"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {emailSending ? 'Sending…' : 'Send via Email'}
                </button>
                {smsConfigured && (
                  <button
                    onClick={sendViaSms}
                    disabled={smsSending}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-green-300 rounded-lg text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    {smsSending ? 'Sending…' : 'Send via SMS'}
                  </button>
                )}
                <span className="text-xs text-gray-500">or copy the link above</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reconcile drafts with Jobber ──────────────────────────────────────────────
// Michael bills these customers in Jobber; the app's draft invoices should just
// mirror Jobber's status. Paste the customers Jobber shows as PAID — those
// drafts are marked paid, every other draft becomes pending (sent). No KAM
// payment is recorded, because Jobber is where the money is collected.
const JOBBER_PAID_SEED = [
  'Ali', 'Alan', 'Aubrey Rushing', 'Bhumi', 'Chris McSorely', 'Christian',
  'Colby Malek', 'Dalton Sheffield', 'David 204 Choke Canyon', 'David Franklin',
  'David Klein', 'Heather Fullerton', 'Jeff Russell', 'Jessey Yen',
  'Joshua Carnett', 'Judith Brown', 'Keren Assouline', 'Kyle Watson',
  'Matt McLaughlin', 'Megan', 'Natalie Friess', 'Olivia Denning',
].join('\n');

const normName = (s: string) =>
  String(s || '').toLowerCase().replace(/\b(mr|mrs|ms|mr\.|mrs\.|ms\.|dr|dr\.)\b/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

function ReconcileJobberModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [paidText, setPaidText] = useState(JOBBER_PAID_SEED);
  const { data, isLoading } = useQuery('reconcile-drafts', () =>
    api.get('/invoices', { params: { status: 'draft' } }).then(r => r.data.invoices as any[]));
  const drafts = data || [];

  const paidSet = new Set(paidText.split('\n').map(normName).filter(Boolean));
  const plan = drafts.map(d => ({ ...d, target: paidSet.has(normName(d.customer_name)) ? 'paid' : 'sent' }));
  const paidCount = plan.filter(p => p.target === 'paid').length;
  const pendingCount = plan.length - paidCount;

  const apply = useMutation(
    () => api.post('/invoices/bulk-status', { updates: plan.map(p => ({ id: p.id, status: p.target })) }).then(r => r.data),
    {
      onSuccess: (r: any) => { toast.success(r?.message || 'Reconciled'); onDone(); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not reconcile'); },
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-gray-900">Reconcile drafts with Jobber</p>
          <p className="text-xs text-gray-500 mt-0.5">Paste the customers Jobber shows as <b>Paid</b> — one per line. Those drafts get marked paid; the rest become pending. Nothing is charged.</p>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          <textarea value={paidText} onChange={e => setPaidText(e.target.value)} rows={8}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-400"
            placeholder="Paste paid customer names, one per line" />
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading drafts…</p>
          ) : (
            <div className="text-sm text-gray-700">
              <p><b>{drafts.length}</b> draft invoices · <span className="text-green-700 font-semibold">{paidCount} → paid</span> · <span className="text-gray-600 font-semibold">{pendingCount} → pending</span></p>
              <ul className="mt-2 max-h-40 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                {plan.map(p => (
                  <li key={p.id} className="flex items-center justify-between px-3 py-1.5 text-xs">
                    <span className="text-gray-800">{p.customer_name} <span className="text-gray-500">${Number(p.total).toFixed(0)}</span></span>
                    <span className={p.target === 'paid' ? 'text-green-700 font-semibold' : 'text-gray-500'}>{p.target === 'paid' ? 'Paid' : 'Pending'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-600 rounded-lg border border-gray-300">Cancel</button>
          <button onClick={() => apply.mutate()} disabled={apply.isLoading || drafts.length === 0}
            className="press px-3 py-2 text-sm font-semibold text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
            {apply.isLoading ? 'Applying…' : `Mark ${paidCount} paid, ${pendingCount} pending`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Gift cards: issue store credit and see balances ───────────────────────────
function GiftCardsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery('gift-cards', () => api.get('/gift-cards').then(r => r.data.cards as any[]));
  const cards = data || [];
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');

  const issue = useMutation(
    () => api.post('/gift-cards', { amount: Number(amount), recipient: recipient.trim() || null }).then(r => r.data),
    {
      onSuccess: (r: any) => { toast.success(r?.message || 'Created'); setAmount(''); setRecipient(''); qc.invalidateQueries('gift-cards'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not create'); },
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="font-semibold text-gray-900">Gift cards</p>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600 text-sm">Close</button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-500">Amount
              <input value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder="$50"
                className="block mt-0.5 border border-gray-300 rounded-lg px-3 py-2 text-sm w-28" />
            </label>
            <label className="text-xs text-gray-500 flex-1 min-w-[10rem]">Recipient (optional)
              <input value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="e.g. for the Smiths"
                className="block mt-0.5 border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
            </label>
            <button onClick={() => issue.mutate()} disabled={issue.isLoading || !(Number(amount) > 0)}
              className="press px-3 py-2 text-sm font-semibold text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
              {issue.isLoading ? 'Creating…' : 'Issue card'}
            </button>
          </div>
          <p className="text-[11px] text-gray-500">Redeem a card on an invoice with “Apply gift card”. No card is charged here — collect for the gift card however you like.</p>
          <div>
            {cards.length === 0 ? (
              <p className="text-sm text-gray-500">No gift cards yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {cards.map((c: any) => (
                  <li key={c.id} className="py-2 flex items-center justify-between text-sm">
                    <span>
                      <span className="font-mono font-semibold text-gray-800">{c.code}</span>
                      {c.recipient && <span className="text-gray-500"> · {c.recipient}</span>}
                    </span>
                    <span className={c.status === 'used' ? 'text-gray-500' : 'text-green-700 font-semibold'}>
                      ${Number(c.balance).toFixed(2)} {c.status === 'used' ? 'used' : `of $${Number(c.initial_amount).toFixed(0)}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
